import React, { useState, useEffect } from 'react'
import { X, Copy, AlertCircle } from 'lucide-react'

const CloneTemplateDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  originalTemplateName = '',
  isCloning = false 
}) => {
  const [newName, setNewName] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      // Set default name when dialog opens
      setNewName(`${originalTemplateName} (Copy)`)
      setError('')
    }
  }, [isOpen, originalTemplateName])

  const handleConfirm = () => {
    const trimmedName = newName.trim()
    
    if (!trimmedName) {
      setError('Template name is required')
      return
    }
    
    if (trimmedName.length > 255) {
      setError('Template name must be less than 255 characters')
      return
    }
    
    onConfirm(trimmedName)
  }

  const handleClose = () => {
    if (!isCloning) {
      onClose()
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isCloning) {
      handleConfirm()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Copy className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Clone Template
            </h2>
          </div>
          {!isCloning && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              Enter a name for the cloned template:
            </p>
            
            <div>
              <label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-2">
                Template Name
              </label>
              <input
                id="template-name"
                type="text"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value)
                  setError('')
                }}
                onKeyPress={handleKeyPress}
                disabled={isCloning}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  error ? 'border-red-500' : 'border-gray-300'
                } ${isCloning ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="Enter template name"
                maxLength={255}
                autoFocus
              />
              
              {error && (
                <div className="flex items-center space-x-2 mt-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            {!isCloning && (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={isCloning || !newName.trim()}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isCloning && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              <span>{isCloning ? 'Cloning...' : 'Clone Template'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CloneTemplateDialog

